import { type PropertyT } from '@ruminati/types_v2/property'
import { type VisionPropertySummaryT } from '@ruminati/types_v2/property'
import { groupBy } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useEmissionsReportsStore } from './emissionsReports'
import { useOrganisationStore } from './organisations'

// NOTE: This is a single reference to some consolidated property types that we'll use across the app
// They should provide a consistent interface that works for all org types
export type CompliantVisionPropertyType = VisionPropertySummaryT & {
  name: string
  agriwebbFarmId: undefined
  pic: undefined
  ngr: undefined
}
export type VaryingPropertyTypes = PropertyT | CompliantVisionPropertyType

export const usePropertiesStore = defineStore('properties', () => {
  const organisationStore = useOrganisationStore()
  const emissionsReportsStore = useEmissionsReportsStore()

  const properties = ref<Array<PropertyT> | undefined>()

  function setProperties(newProperties: Array<PropertyT>) {
    properties.value = newProperties
  }

  function addOrReplaceProperty(property: PropertyT) {
    if (!properties.value) {
      properties.value = [property]
      return
    }

    const existingPropertyIndex = properties.value.findIndex((p) => p.id === property.id)
    if (existingPropertyIndex > -1) {
      properties.value.splice(existingPropertyIndex, 1, property)
    } else {
      properties.value.push(property)
    }
  }

  function removeProperty(propertyId: string) {
    if (properties.value) {
      const idx = properties.value.findIndex((p) => p.id === propertyId)
      if (idx > -1) {
        properties.value.splice(idx, 1)
      }
    }

    if (emissionsReportsStore.emissionsReports) {
      emissionsReportsStore.emissionsReports.forEach((r) => {
        if (r.propertyId === propertyId) {
          const actualIndex = emissionsReportsStore.emissionsReports?.findIndex(
            (r2) => r.id === r2.id,
          )
          if (actualIndex !== undefined && actualIndex > -1)
            emissionsReportsStore.emissionsReports?.splice(actualIndex, 1)
        }
      })
    }
  }

  const propertiesWithReportSummaries = computed(() => {
    const reportsByPropertyId = emissionsReportsStore.emissionsReportsByPropertyId

    return properties.value?.map((p) => {
      const propertyId = p.id
      const reportSummaries = reportsByPropertyId[propertyId] ?? []
      return { ...p, reportSummaries }
    })
  })

  const propertiesWithReportSummariesByOrgId = computed(() => {
    if (!propertiesWithReportSummaries.value) {
      return {}
    } else {
      return groupBy(propertiesWithReportSummaries.value, 'organisationId')
    }
  })

  const isMultiEnterpriseOrg = computed(() => {
    if (!properties.value) return false
    return properties.value.length > 1
  })

  const isMultiStateOrg = computed(() => {
    if (!properties.value) return false
    return new Set(properties.value.map((p) => p.stateId)).size > 1
  })

  // TODO: can callers of this use something like tbMapToLabelAndValue instead? (a non-TB version of the util function)
  const propertiesAsOptions = computed(() => {
    return (properties.value ?? []).map((p) => {
      return {
        ...p,
        value: p.id,
        label: p.name,
      }
    })
  })

  function resetAll() {
    properties.value = undefined
  }

  watch(
    () => organisationStore.currentOrgId,
    () => {
      resetAll()
    },
  )

  return {
    properties,
    setProperties,
    addOrReplaceProperty,
    removeProperty,

    propertiesWithReportSummaries,
    propertiesWithReportSummariesByOrgId,
    isMultiEnterpriseOrg,
    isMultiStateOrg,
    propertiesAsOptions,

    resetAll,
  }
})
